import { lazy, Suspense } from 'react';

import LoadingSpinner from '../components/LoadingSpinner';

const PageLoader = () => <LoadingSpinner fullScreen />;

export const LazyRoute = ({
  Component,
  ...props
}: {
  Component: React.LazyExoticComponent<React.ComponentType<any>>;
  [key: string]: any;
}) => (
  <Suspense fallback={<PageLoader />}>
    <Component {...props} />
  </Suspense>
);

// ---- Core pages -----------------------------------------------------------
export const Home = lazy(() => import('../pages/home/Home').then((m) => ({ default: m.Home })));
export const Contact = lazy(() =>
  import('../pages/contact/Contact').then((m) => ({ default: m.Contact }))
);
export const Project = lazy(() =>
  import('../pages/project/Project').then((m) => ({ default: m.Project }))
);
export const Search = lazy(() =>
  import('../pages/search/Search').then((m) => ({ default: m.Search }))
);
export const SupplyLink = lazy(() =>
  import('../pages/supplylink/SupplyLink').then((m) => ({ default: m.SupplyLink }))
);
export const WhereToBuy = lazy(() =>
  import('../pages/whereToBuy/WhereToBuy').then((m) => ({ default: m.WhereToBuy }))
);

// ---- Project sub-pages ----------------------------------------------------
export const EnvironmentalAndSocialRisks = lazy(() =>
  import('../pages/project/environmental_and_social_risks/EnvironmentalAndSocialRisks').then(
    (m) => ({ default: m.EnvironmentalAndSocialRisks })
  )
);
export const ExecutiveSummary = lazy(() =>
  import('../pages/project/executive_summary/ExecutiveSummary').then((m) => ({
    default: m.ExecutiveSummary,
  }))
);
export const GhgRating = lazy(() =>
  import('../pages/project/ghg_rating/GHGRating').then((m) => ({ default: m.GhgRating }))
);
export const RelatedContent = lazy(() =>
  import('../pages/project/RelatedContent').then((m) => ({ default: m.RelatedContent }))
);
export const SDGRating = lazy(() =>
  import('../pages/project/sdg_rating/SDGRating').then((m) => ({ default: m.SDGRating }))
);
export const PricingAndSupply = lazy(() =>
  import('../pages/project/pricing_and_supply/PricingAndSupply').then((m) => ({
    default: m.PricingAndSupply,
  }))
);

// ---- Retiree --------------------------------------------------------------
export const RetireeInsights = lazy(() =>
  import('../pages/retireeInsights/RetireeInsights').then((m) => ({ default: m.RetireeInsights }))
);
export const RetireeInsightsDrillDown = lazy(() =>
  import('../pages/retireeDrillDown/RetireeInsightsDrillDown').then((m) => ({
    default: m.RetireeInsightsDrillDown,
  }))
);

// ---- Insights Hub ---------------------------------------------------------
export const InsightsHub = lazy(() =>
  import('../pages/insightsHub/InsightsHub').then((m) => ({ default: m.InsightsHub }))
);
export const InsightsHubArticle = lazy(() =>
  import('../pages/insightsHubArticle/InsightsHubArticle').then((m) => ({
    default: m.InsightsHubArticle,
  }))
);
export const InsightsHubCategory = lazy(() =>
  import('../pages/insightsHubCategory/InsightsHubCategory').then((m) => ({
    default: m.InsightsHubCategory,
  }))
);
export const InsightsHubPinnedPost = lazy(() =>
  import('../pages/insightsHubArticle/InsightsHubPinnedPost').then((m) => ({
    default: m.InsightsHubPinnedPost,
  }))
);
export const InsightsHubResearchUpdate = lazy(() =>
  import('../pages/insightsHubResearchUpdate/InsightsHubResearchUpdate').then((m) => ({
    default: m.InsightsHubResearchUpdate,
  }))
);

// ---- Compass --------------------------------------------------------------
export const Compass = lazy(() =>
  import('../pages/compass/Compass').then((m) => ({ default: m.Compass }))
);
export const ProjectType = lazy(() =>
  import('../pages/compass/components/projectType/ProjectType').then((m) => ({
    default: m.ProjectType,
  }))
);
export const RootOverview = lazy(() =>
  import('../pages/compass/components/projectType/Renderer/Overview').then((m) => ({
    default: m.Overview,
  }))
);
export const RootSupplyAndDemand = lazy(() =>
  import('../pages/compass/components/projectType/Renderer/SupplyAndDemand').then((m) => ({
    default: m.SupplyAndDemand,
  }))
);
export const RootTopGHGRisks = lazy(() =>
  import('../pages/compass/components/projectType/Renderer/TopGHGRisks').then((m) => ({
    default: m.TopGHGRisks,
  }))
);
export const RootTopGHGRisksDetail = lazy(() =>
  import('../pages/compass/components/projectType/Renderer/TopGHGRisksDetail').then((m) => ({
    default: m.TopGHGRisksDetail,
  }))
);
export const RootMethodologyOverview = lazy(() =>
  import('../pages/compass/components/projectType/Renderer/methodologyDetail/Overview').then(
    (m) => ({ default: m.Overview })
  )
);
export const MethodologyDetail = lazy(() =>
  import('../pages/compass/components/projectType/MethodologyDetail').then((m) => ({
    default: m.MethodologyDetail,
  }))
);
export const RootMethodologyArea = lazy(() =>
  import('../pages/compass/components/projectType/Renderer/methodologyDetail/MethodologyArea').then(
    (m) => ({ default: m.MethodologyArea })
  )
);
export const RootScope = lazy(() =>
  import('../pages/compass/components/projectType/Renderer/methodologyDetail/Scope').then((m) => ({
    default: m.Scope,
  }))
);
export const RootCorsia = lazy(() =>
  import(
    '../pages/compass/components/projectType/Renderer/methodologyDetail/CorsiaEligibility'
  ).then((m) => ({ default: m.CorsiaEligibility }))
);

// ---- Project Screener -----------------------------------------------------
export const ProjectScreener = lazy(() =>
  import('../pages/projectScreener/ProjectScreener').then((m) => ({
    default: m.ProjectScreener,
  }))
);
export const NewScreening = lazy(() =>
  import('../pages/projectScreener/NewScreening').then((m) => ({ default: m.NewScreening }))
);
export const ScreeningResults = lazy(() =>
  import('../pages/projectScreener/ScreeningResults').then((m) => ({
    default: m.ScreeningResults,
  }))
);
export const ScreenerFAQ = lazy(() =>
  import('../pages/projectScreener/ScreenerFAQ').then((m) => ({ default: m.ScreenerFAQ }))
);

// ---- Auth / Misc ----------------------------------------------------------
export const SignUp = lazy(() =>
  import('../pages/signup/SignUp').then((m) => ({ default: m.SignUp }))
);
export const Register = lazy(() =>
  import('../pages/register/Register').then((m) => ({ default: m.Register }))
);
export const AccountRequest = lazy(() =>
  import('../pages/accountRequest/AccountRequest').then((m) => ({ default: m.AccountRequest }))
);
export const DeveloperPortal = lazy(() =>
  import('../pages/developerPortal/DeveloperPortal').then((m) => ({ default: m.DeveloperPortal }))
);
export const Upgrade = lazy(() =>
  import('../pages/upgrade/Upgrade').then((m) => ({ default: m.Upgrade }))
);
export const Programs = lazy(() =>
  import('../pages/programs/Programs').then((m) => ({ default: m.Programs }))
);
export const FreeDashboard = lazy(() => import('../pages/freeDashboard/FreeDashboard'));
export const ExpertAdvisory = lazy(() =>
  import('../pages/expertAdvisory/ExpertAdvisory').then((m) => ({ default: m.ExpertAdvisory }))
);
export const ExpertAdvisoryLearnMore = lazy(() =>
  import('../pages/expertAdvisory/ExpertAdvisoryLearnMore').then((m) => ({
    default: m.ExpertAdvisoryLearnMore,
  }))
);
